
import AnalysisAPI from '@/api/resources/Analysis.api.ts'
import { Card, CardContent } from '@/components/ui/card'
import Loader from '@/components/ui/loader.tsx'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip.tsx'
import useDocumentTitle from '@/hooks/use-document-title.ts'
import { Dictionary } from '@/types/field.js'
import { errorToast, successToast } from '@/utils/alerts.tsx'
import { AxiosError } from 'axios'
import { Briefcase, Calendar, Check, Clock, Copy, DollarSign, ExternalLink, Globe, GraduationCap, LogInIcon, LucideIcon, Mail, MessageCircle, NotepadText, SearchX, Shield, TimerReset, University, User } from 'lucide-react'
import React, { useState } from 'react'
import { Navigate, useMatch, useParams } from 'react-router-dom'
interface ProfileFieldProps {
    icon: LucideIcon;
    label: string;
    value: string | null;
    image?: string;
    valueClass?: string
}


const ProfileField = ({ icon: Icon, label, value, image, valueClass }: ProfileFieldProps) => (
    <div className="bg-white rounded-lg pt-4 duration-200">
        <div className="flex items-start space-x-3 pl-2">
            {
                image ? (
                    <img src={image} alt="User" className="w-9 h-9 rounded-full object-cover" />
                ) : (
                    <div className="p-2 pl-0 bg-gray-50 rounded-lg">
                        <Icon className="w-5 h-5 text-gray-500" />
                    </div>
                )
            }
            <div className="flex-1">
                <label className="block text-sm font-medium text-gray-600 mb-1">{label}</label>
                <p className={`text-gray-900 font-medium ${valueClass || ''}`}>{value || '-'}</p>
            </div>
        </div>
    </div>
)
const ResultsSection = () => {
    const path = useMatch('/user/:id/:screenshotId')
    const { id } = useParams()

    if (path) {
        useDocumentTitle('Analysis Details', 'User Details', `/user/${id}`)
    }
    else {
        useDocumentTitle('Analysis Details', 'Analysis History', '/analysis-history')
    }
    const [results, setResults] = useState<Dictionary>({})
    const [loading, setLoading] = useState(false)
    const { screenshotId } = useParams()

    const loadDetails = async (id: string) => {
        setLoading(true)
        try {
            const response = await AnalysisAPI.analysisGet(id)
            if (response.data.success === 1) {
                const UserData = response.data.data
                setResults(UserData)
            } else {
                if (response.data.message) {
                    errorToast(response.data.message)
                } else {
                    errorToast('Analysis details not found')
                }
            }
        } catch (error: unknown) {
            if (error instanceof AxiosError) {
                errorToast(error.response?.data?.message || 'Error occurred in getting analysis data')
            } else {
                errorToast('Error occurred in getting analysis data')
            }
        } finally {
            setLoading(false)
        }
    }

    React.useEffect(() => {
        if (screenshotId) {
            loadDetails(screenshotId)
        }
    }, [screenshotId])

    if (!screenshotId) {
        return <Navigate to="/analysis-history" />
    }

    if (loading) {
        return <Loader />
    }

    if (!results) return null

    const getCriminalProfileList = results?.profile_match_images?.filter((i: any) => i.profile_type === 'Criminal') || []

    const getSocialProfileList = results?.profile_match_images?.filter((i: any) => !(i.profile_type === 'Criminal')) || []

    const getSocialPostList = results?.profile_match_images?.filter((i: any) => (!i.profile_type)) || []

    return (
        <>
            <div className="space-y-4 min-h-[300px] mb-4">
                <div className="bg-white rounded-xl mb-16">
                    <div className="pt-2">
                        <div className="flex flex-row gap-24">
                            <ProfileField
                                icon={User}
                                label="Full Name"
                                value={results?.user_details?.name ?? null}
                            />

                            {results?.user_details?.email && (
                                <ProfileField
                                    icon={Mail}
                                    label="Email Address"
                                    value={results?.user_details?.email}
                                />
                            )}

                            {results?.user_details?.login_type && (
                                <ProfileField
                                    icon={LogInIcon}
                                    label="Login Type"
                                    value={results?.user_details?.login_type.toLowerCase()}
                                    valueClass='capitalize'
                                />
                            )}

                            {results?.user_details?.plan?.title && (
                                <ProfileField
                                    icon={TimerReset}
                                    label="Subscription Plan"
                                    value={results?.user_details?.plan?.title?.toLowerCase()}
                                    valueClass='capitalize'
                                />
                            )}

                            {results?.user_details?.subscription_status && (
                                <ProfileField
                                    icon={NotepadText}
                                    label="Subscription Status"
                                    value={results?.user_details?.subscription_status?.toLowerCase()}
                                    valueClass='capitalize'
                                />
                            )}

                            {results?.user_details?.createdAt && (
                                <ProfileField
                                    icon={Clock}
                                    label="Registered At"
                                    value={new Date(results?.user_details?.createdAt)
                                        .toLocaleString('en-US', {
                                            timeZone: 'UTC',
                                            year: 'numeric',
                                            month: 'long',
                                            day: '2-digit',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                            hour12: true
                                        })
                                        .replace(',', '')
                                        .replace(/(\d{2}) (\d{4})/, '$1, $2')
                                        .replace(/^(\w+ \d{2}, \d{4}) AT /, '$1 at ')
                                    }
                                />
                            )}
                        </div>
                    </div>
                </div>

                {results?.image_url && <div className="relative rounded-xl overflow-hidden">
                    <div className="relative">
                        <img
                            src={results?.image_url}
                            alt="Profile screenshot"
                            className="w-full h-[300px] object-contain max-h-[400px] rounded"
                        />
                    </div>
                </div>}

                {results.createdAt && <div className="flex">
                    <div className="w-full flex justify-end items-center text-sm font-medium text-gray-500">
                        <Calendar className="mr-1 h-4 w-4 text-gray-400" />
                        {new Date(results.createdAt)
                            .toLocaleString('en-US', {
                                timeZone: 'UTC',
                                year: 'numeric',
                                month: 'long',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true
                            })
                            .replace(',', '')
                            .replace(/(\d{2}) (\d{4})/, '$1, $2')
                            .replace(/^(\w+ \d{2}, \d{4}) AT /, '$1 at ')}
                        {/* {moment(results.createdAt).format('MM/DD/YY hh:mm a')} */}
                    </div>
                </div>}
            </div>
            <div className="mt-8">
                {/* Personal Information Section */}
                {results?.is_personal_info && <Card className='mb-6'>
                    <CardContent className="p-6 space-y-4">
                        <div className="flex items-center mb-2">
                            <User className="text-blue-600 mr-2" size={20} />
                            <h3 className="text-xl font-semibold">Personal Information</h3>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-1">
                                <p className="text-sm text-gray-500">Full Name</p>
                                <div className='flex items-center'>
                                    <User size={16} className="text-gray-500 mr-1" />
                                    <p className="font-medium">{(results?.first_name || results?.last_name) ? `${results?.first_name ?? ''} ${results?.last_name ?? ''}` : '-'}</p>
                                </div>
                            </div>


                            <div className="space-y-1">
                                <p className="text-sm text-gray-500">Job Title</p>
                                <div className="flex items-center">
                                    <Briefcase size={16} className="text-gray-500 mr-1" />
                                    <p className="font-medium">{results?.job_title || '-'}</p>
                                </div>
                            </div>

                            <div className="space-y-1">
                                <p className="text-sm text-gray-500">Current Company</p>
                                <p className="font-medium">{results?.company_name || '-'}</p>
                            </div>


                            <div className="space-y-1">
                                <p className="text-sm text-gray-500">Projected Salary</p>
                                <div className="flex items-center">
                                    <DollarSign size={16} className="text-gray-500 mr-1" />
                                    <p className="font-medium">{results?.projected_salary ? results.projected_salary.replace(/\$/g, '') : '-'}</p>
                                </div>
                            </div>

                            <div className="space-y-1">
                                <p className="text-sm text-gray-500">Education</p>
                                <div className="flex items-center">
                                    <GraduationCap size={16} className="text-gray-500 mr-1" />
                                    <p className="font-medium">{results.education || '-'}</p>
                                </div>
                            </div>

                            <div className="space-y-1">
                                <p className="text-sm text-gray-500">University</p>
                                <div className="flex items-center">
                                    <University size={16} className="text-gray-500 mr-1" />
                                    <p className="font-medium">
                                        {results?.university || '-'}
                                    </p>
                                </div>
                            </div>

                        </div>
                    </CardContent>
                </Card>}

                {/* Social Media Posts Section */}
                {results?.profile_match_images?.length > 0 ?
                    <>
                        {/* Social Profile */}
                        {/* <RenderList title={'Social Media Profiles'} item={getSocialProfileList} icon={<User className="mr-2" size={20} />} is_criminal={false} /> */}
                        <RenderSocialProfilesWithSubsections socialProfiles={getSocialProfileList} />

                        {/* Social Posts */}
                        <RenderList title={'Social Media Posts'} item={getSocialPostList} icon={<MessageCircle className="mr-2" size={20} />} is_criminal={false} />

                        {/* Criminal Posts  */}
                        <RenderList title={'Criminal Background'} item={getCriminalProfileList} icon={<Shield className="mr-2" size={20} />} is_criminal={true} />

                    </>
                    : <Card>
                        <CardContent className="flex flex-col items-center justify-center py-16">
                            <div className="rounded-full bg-gray-100 p-4 mb-4">
                                <SearchX className="h-10 w-10 text-gray-400" />
                            </div>
                            <h3 className="text-xl font-medium text-gray-700 mb-2">Details not found!</h3>
                            <p className="text-gray-500 text-center max-w-md mb-6">
                            </p>
                        </CardContent>
                    </Card>}
            </div>
        </>
    )
}

export default ResultsSection

const icon: any = {
    'instagram.com': <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20" height="20" viewBox="0 0 48 48">
        <radialGradient id="yOrnnhliCrdS2gy~4tD8ma_Xy10Jcu1L2Su_gr1" cx="19.38" cy="42.035" r="44.899" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#fd5"></stop><stop offset=".328" stopColor="#ff543f"></stop><stop offset=".348" stopColor="#fc5245"></stop><stop offset=".504" stopColor="#e64771"></stop><stop offset=".643" stopColor="#d53e91"></stop><stop offset=".761" stopColor="#cc39a4"></stop><stop offset=".841" stopColor="#c837ab"></stop></radialGradient><path fill="url(#yOrnnhliCrdS2gy~4tD8ma_Xy10Jcu1L2Su_gr1)" d="M34.017,41.99l-20,0.019c-4.4,0.004-8.003-3.592-8.008-7.992l-0.019-20	c-0.004-4.4,3.592-8.003,7.992-8.008l20-0.019c4.4-0.004,8.003,3.592,8.008,7.992l0.019,20	C42.014,38.383,38.417,41.986,34.017,41.99z"></path><radialGradient id="yOrnnhliCrdS2gy~4tD8mb_Xy10Jcu1L2Su_gr2" cx="11.786" cy="5.54" r="29.813" gradientTransform="matrix(1 0 0 .6663 0 1.849)" gradientUnits="userSpaceOnUse"><stop offset="0" stopColor="#4168c9"></stop><stop offset=".999" stopColor="#4168c9" stopOpacity="0"></stop></radialGradient><path fill="url(#yOrnnhliCrdS2gy~4tD8mb_Xy10Jcu1L2Su_gr2)" d="M34.017,41.99l-20,0.019c-4.4,0.004-8.003-3.592-8.008-7.992l-0.019-20	c-0.004-4.4,3.592-8.003,7.992-8.008l20-0.019c4.4-0.004,8.003,3.592,8.008,7.992l0.019,20	C42.014,38.383,38.417,41.986,34.017,41.99z"></path><path fill="#fff" d="M24,31c-3.859,0-7-3.14-7-7s3.141-7,7-7s7,3.14,7,7S27.859,31,24,31z M24,19c-2.757,0-5,2.243-5,5	s2.243,5,5,5s5-2.243,5-5S26.757,19,24,19z"></path><circle cx="31.5" cy="16.5" r="1.5" fill="#fff"></circle><path fill="#fff" d="M30,37H18c-3.859,0-7-3.14-7-7V18c0-3.86,3.141-7,7-7h12c3.859,0,7,3.14,7,7v12	C37,33.86,33.859,37,30,37z M18,13c-2.757,0-5,2.243-5,5v12c0,2.757,2.243,5,5,5h12c2.757,0,5-2.243,5-5V18c0-2.757-2.243-5-5-5H18z"></path>
    </svg>,
    'facebook.com': <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20" height="20" viewBox="0 0 48 48">
        <path fill="#039be5" d="M24 5A19 19 0 1 0 24 43A19 19 0 1 0 24 5Z"></path><path fill="#fff" d="M26.572,29.036h4.917l0.772-4.995h-5.69v-2.73c0-2.075,0.678-3.915,2.619-3.915h3.119v-4.359c-0.548-0.074-1.707-0.236-3.897-0.236c-4.573,0-7.254,2.415-7.254,7.917v3.323h-4.701v4.995h4.701v13.729C22.089,42.905,23.032,43,24,43c0.875,0,1.729-0.08,2.572-0.194V29.036z"></path>
    </svg>,
    'tiktok.com': <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20" height="20" viewBox="0 0 50 50"> <path d="M41,4H9C6.243,4,4,6.243,4,9v32c0,2.757,2.243,5,5,5h32c2.757,0,5-2.243,5-5V9C46,6.243,43.757,4,41,4z M37.006,22.323 c-0.227,0.021-0.457,0.035-0.69,0.035c-2.623,0-4.928-1.349-6.269-3.388c0,5.349,0,11.435,0,11.537c0,4.709-3.818,8.527-8.527,8.527 s-8.527-3.818-8.527-8.527s3.818-8.527,8.527-8.527c0.178,0,0.352,0.016,0.527,0.027v4.202c-0.175-0.021-0.347-0.053-0.527-0.053 c-2.404,0-4.352,1.948-4.352,4.352s1.948,4.352,4.352,4.352s4.527-1.894,4.527-4.298c0-0.095,0.042-19.594,0.042-19.594h4.016 c0.378,3.591,3.277,6.425,6.901,6.685V22.323z"></path> </svg>,
    'linkedin.com': <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20" height="20" viewBox="0 0 48 48"><path fill="#0288D1" d="M42,37c0,2.762-2.238,5-5,5H11c-2.761,0-5-2.238-5-5V11c0-2.762,2.239-5,5-5h26c2.762,0,5,2.238,5,5V37z"></path><path fill="#FFF" d="M12 19H17V36H12zM14.485 17h-.028C12.965 17 12 15.888 12 14.499 12 13.08 12.995 12 14.514 12c1.521 0 2.458 1.08 2.486 2.499C17 15.887 16.035 17 14.485 17zM36 36h-5v-9.099c0-2.198-1.225-3.698-3.192-3.698-1.501 0-2.313 1.012-2.707 1.99C24.957 25.543 25 26.511 25 27v9h-5V19h5v2.616C25.721 20.5 26.85 19 29.738 19c3.578 0 6.261 2.25 6.261 7.274L36 36 36 36z"></path></svg>,
    'youtube.com': <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20" height="20" viewBox="0 0 48 48"><path fill="#FF3D00" d="M43.2,33.9c-0.4,2.1-2.1,3.7-4.2,4c-3.3,0.5-8.8,1.1-15,1.1c-6.1,0-11.6-0.6-15-1.1c-2.1-0.3-3.8-1.9-4.2-4C4.4,31.6,4,28.2,4,24c0-4.2,0.4-7.6,0.8-9.9c0.4-2.1,2.1-3.7,4.2-4C12.3,9.6,17.8,9,24,9c6.2,0,11.6,0.6,15,1.1c2.1,0.3,3.8,1.9,4.2,4c0.4,2.3,0.9,5.7,0.9,9.9C44,28.2,43.6,31.6,43.2,33.9z"></path><path fill="#FFF" d="M20 31L20 17 32 24z"></path></svg>,
    'twitter.com': <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="20" height="20" viewBox="0 0 50 50"><path d="M 5.9199219 6 L 20.582031 27.375 L 6.2304688 44 L 9.4101562 44 L 21.986328 29.421875 L 31.986328 44 L 44 44 L 28.681641 21.669922 L 42.199219 6 L 39.029297 6 L 27.275391 19.617188 L 17.933594 6 L 5.9199219 6 z M 9.7167969 8 L 16.880859 8 L 40.203125 42 L 33.039062 42 L 9.7167969 8 z"></path></svg>,
    'default': <Globe size={20} className="text-black" />
}

const checkDomain = (url: string) => {
    if (!url) return null
    const subDomain = new URL(url).hostname
    const domain = subDomain?.split('.')?.slice(-2)?.join('.') || 'default'
    return <span className="flex justify-center items-center">{icon[domain] || icon.default}</span>
}
const RenderList = ({ title, item, icon, is_criminal = false }: { title: string, item: any, icon: any, is_criminal: boolean }) => {
    if (!item.length && !is_criminal) return null
    return <div className='mb-10'>
        <div >
            <div className="flex items-center mb-5">
                {icon}
                <h3 className="text-xl font-semibold">{title} {item?.length > 0 && `(${item?.length})`} </h3>
            </div>

            {item.length > 0 && <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 :grid-cols-5 gap-5">
                {item?.map((post: any, index: number) => (
                    <Card key={index} className={`overflow-hidden hover:shadow-md transition-shadow border ${post.is_primary_profile && 'border-blue-700'} `}>
                        <div className="relative h-48 bg-gray-100">
                            <img
                                src={post.image_url}
                                alt={`${post.platform} post`}
                                className="w-full h-full object-contain"
                            />
                            <div className="absolute top-2 left-2 bg-white/90 px-2 py-2 rounded-full flex items-center text-xs font-medium">
                                {checkDomain(post.url)}
                            </div>
                            <div className="absolute top-2 right-2 bg-white/90 px-2 py-2 rounded-full flex items-center  font-medium">
                                {post?.score}
                            </div>
                        </div>
                        <div className="p-3 space-y-1 flex justify-between">
                            <div className='flex w-[75%]'>
                                <p key={post._id} className="text-sm font-medium text-blue-600 overflow-hidden break-words line-clamp-3">{post.url}</p>
                            </div>
                            <div className='flex gap-3 justify-center items-center w-[20%] p-2'>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span className='cursor-pointer text-gray-500'><Copy onClick={() => {
                                            navigator.clipboard.writeText(post.url)
                                            successToast('Link has been copied to clipboard')
                                        }} /></span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Copy</p>
                                    </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <span className='cursor-pointer text-gray-500'><ExternalLink onClick={() => { window.open(post.url, '_blank', 'noopener,noreferrer') }} /></span>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Visit Link</p>
                                    </TooltipContent>
                                </Tooltip>

                            </div>
                        </div>
                    </Card>
                ))}
            </div>}
            {(is_criminal && !(item.length > 0)) && <div className="flex items-center text-green-600 p-3 bg-green-50 rounded-lg">
                <Check size={20} className="mr-2" />
                <p className="font-medium">No Mugshots Found</p>
            </div>}
        </div>
    </div>
}

const platformNames: any = {
    'instagram': 'Instagram',
    'facebook': 'Facebook',
    'tiktok': 'TikTok',
    'linkedin': 'LinkedIn',
    'twitter': 'Twitter'
}

const RenderSocialProfilesWithSubsections = ({ socialProfiles }) => {

    if (!socialProfiles.length) return null

    const profilesByPlatform: any = {}
    for (const profile of socialProfiles) {

        const platform = profile.profile_type
        if (platform) {
            if (!profilesByPlatform[platform]) {
                profilesByPlatform[platform] = []
            }
            profilesByPlatform[platform].push(profile)
        }
    }

    const totalValidProfiles: any = Object.values(profilesByPlatform).reduce((acc, arr) => acc + arr.length, 0)
    if (!totalValidProfiles) return null

    return (
        <div className='mb-10'>
            <div className="flex items-center mb-5">
                <User className="mr-2" size={20} />
                <h3 className="text-xl font-semibold">Social Media Profiles ({totalValidProfiles})</h3>
            </div>

            {Object.entries(profilesByPlatform).map(([platform, profiles]: any, idx) => (
                <div key={platform} className="mb-8">
                    {idx !== 0 && <hr className="border-gray-300 mb-6" />}
                    <div className="flex items-center mb-4 px-4 py-2 bg-gray-50 rounded-md shadow-sm">
                        <div className="text-blue-600">
                            {checkDomain(profiles[0]?.url)}
                        </div>
                        <h4 className="text-lg font-semibold ml-2 text-gray-800">
                            {platformNames[platform] || platform.charAt(0).toUpperCase() + platform.slice(1)} ({profiles.length})
                        </h4>
                    </div>

                    {/* <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 pl-4"> */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 :grid-cols-5 gap-5">
                        {profiles.map((post: any, index: any) => (
                            <Card key={index} className={`overflow-hidden hover:shadow-md transition-shadow border ${post.is_primary_profile && 'border-blue-700'}`}>
                                <div className="relative h-48 bg-gray-100">
                                    <img
                                        src={post.image_url}
                                        alt={`${post.platform} profile`}
                                        className="w-full h-full object-contain"
                                    />
                                    <div className="absolute top-2 left-2 bg-white/90 px-2 py-2 rounded-full flex items-center text-xs font-medium">
                                        {checkDomain(post.url)}
                                    </div>
                                    <div className="absolute top-2 right-2 bg-white/90 px-2 py-2 rounded-full flex items-center font-medium">
                                        {post?.score}
                                    </div>
                                </div>
                                <div className="p-3 space-y-1 flex justify-between">
                                    <div className='flex w-[75%]'>
                                        <p key={post._id} className="text-sm font-medium text-blue-600 overflow-hidden break-words line-clamp-3">{post.url}</p>
                                    </div>
                                    <div className='flex gap-3 justify-center items-center w-[20%] p-2'>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span className='cursor-pointer text-gray-500'>
                                                    <Copy onClick={() => {
                                                        navigator.clipboard.writeText(post.url)
                                                        successToast('Link has been copied to clipboard')
                                                    }} />
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent><p>Copy</p></TooltipContent>
                                        </Tooltip>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <span className='cursor-pointer text-gray-500'>
                                                    <ExternalLink onClick={() => window.open(post.url, '_blank', 'noopener,noreferrer')} />
                                                </span>
                                            </TooltipTrigger>
                                            <TooltipContent><p>Visit Link</p></TooltipContent>
                                        </Tooltip>
                                    </div>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    )
}
